'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TOCItem {
  id: string;
  title: string;
}

interface TableOfContentsProps {
  items: TOCItem[];
}

const TableOfContents = ({ items }: TableOfContentsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      // Check if TOC should be sticky
      setIsSticky(window.scrollY > 300);

      // Find active section
      for (let i = items.length - 1; i >= 0; i--) {
        const element = document.getElementById(items[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(items[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHydrated, items]);

  const scrollToSection = (id: string) => {
    if (!isHydrated) return;
    
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:65:6" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="65" data-component-end-line="77" data-component-file="TableOfContents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-subtle%20p-6%20border%20border-border%22%7D" className="bg-card rounded-lg shadow-subtle p-6 border border-border">
        <h3 data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:66:8" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="66" data-component-end-line="69" data-component-file="TableOfContents.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-heading%20font-semibold%20text-primary%20mb-4%20flex%20items-center%20space-x-2%22%7D" className="text-lg font-heading font-semibold text-primary mb-4 flex items-center space-x-2">
          <Icon data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:67:10" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="67" data-component-end-line="67" data-component-file="TableOfContents.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ListBulletIcon%22%7D" name="ListBulletIcon" size={20} variant="outline" />
          <span data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:68:10" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="68" data-component-end-line="68" data-component-file="TableOfContents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Table%20of%20Contents%22%7D">Table of Contents</span>
        </h3>
        <nav data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:70:8" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="70" data-component-end-line="76" data-component-file="TableOfContents.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
          {items.map((item) => (
            <div data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:72:12" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="72" data-component-end-line="74" data-component-file="TableOfContents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20py-2%20px-3%20rounded-md%22%7D" key={item.id} className="text-sm text-muted-foreground py-2 px-3 rounded-md">
              {item.title}
            </div>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:82:4" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="82" data-component-end-line="106" data-component-file="TableOfContents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`bg-card rounded-lg shadow-subtle border border-border transition-all duration-300 ${
      isSticky ? 'sticky top-20' : ''
    }`}>
      <div data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:85:6" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="85" data-component-end-line="105" data-component-file="TableOfContents.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%22%7D" className="p-6">
        <h3 data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:86:8" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="86" data-component-end-line="89" data-component-file="TableOfContents.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-heading%20font-semibold%20text-primary%20mb-4%20flex%20items-center%20space-x-2%22%7D" className="text-lg font-heading font-semibold text-primary mb-4 flex items-center space-x-2">
          <Icon data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:87:10" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="87" data-component-end-line="87" data-component-file="TableOfContents.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ListBulletIcon%22%7D" name="ListBulletIcon" size={20} variant="outline" />
          <span data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:88:10" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="88" data-component-end-line="88" data-component-file="TableOfContents.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Table%20of%20Contents%22%7D">Table of Contents</span>
        </h3>
        <nav data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:90:8" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="90" data-component-end-line="104" data-component-file="TableOfContents.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
          {items.map((item) => (
            <button data-component-id="src/app/privacy-policy/components/TableOfContents.tsx:92:12" data-component-path="src/app/privacy-policy/components/TableOfContents.tsx" data-component-line="92" data-component-end-line="102" data-component-file="TableOfContents.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`w-full text-left text-sm py-2 px-3 rounded-md transition-all duration-200 ${
                activeSection === item.id
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-foreground hover:bg-muted hover:text-primary'
              }`}
            >
              {item.title}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default TableOfContents;